import { type ReactNode } from 'react';
import { Marker, Popup } from 'react-leaflet';
import { divIcon, type Layer, type LeafletMouseEvent } from 'leaflet';
import {
    getMarkerIconColor,
    getMarkerIconSvgString,
} from '@components/_atoms/projectMarkerIcon';
import { getProjectIcon } from '@utils/projectType';
import { Line } from '@components/_atoms/Line/line';
import { type Project } from '@gql/graphql';
import { useMapContext } from '../_utils/map-context';
import { getProjectStatus } from '../_utils/utils';
import s from './marker.module.scss';

interface ProjectMarkerProps {
    project: Project;
}

export function ProjectMarker({ project }: ProjectMarkerProps): ReactNode {
    const { setProject } = useMapContext();
    const color = getMarkerIconColor(getProjectStatus(project.status?.name));
    const markerIcon = divIcon({
        iconAnchor: [12, 12],
        iconSize: [12, 12],
        html: getMarkerIconSvgString(color),
    });

    if (!project.point) {
        return null;
    }

    return (
        <Marker
            key={project.id}
            position={[project.point.latitude, project.point.longitude]}
            icon={markerIcon}
            eventHandlers={{
                mouseover: (event: LeafletMouseEvent) =>
                    (event.target as Layer).openPopup(),
                mouseout: (event: LeafletMouseEvent) =>
                    (event.target as Layer).closePopup(),
                click: () => {
                    setProject(project);
                },
            }}
        >
            <Popup
                interactive={false}
                className={s.projectPopup}
                closeButton={false}
                offset={[0, -6]}
            >
                <div className="flex flex-row gap-4 justify-content-start align-items-start">
                    <div className="flex flex-column justify-content-start align-items-start gap-4">
                        <h2>{project.name} </h2>
                        <div className="flex flex-column gap-2 justify-content-start align-items-start">
                            <div>{project.customerName}</div>
                            {Boolean(project.organization?.name) && (
                                <div className={s.projectOrganizationName}>
                                    {project.organization?.name}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="flex">
                        {getProjectIcon(project.type?.id)}
                    </div>
                </div>
                <Line />
                <div className="flex justify-content-end">
                    {project.status?.name} - {project.milestone?.name}
                </div>
            </Popup>
        </Marker>
    );
}
